import { useCallback, useEffect, useState } from 'react';
import { matchPath, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Button } from '@/ui/Button';
import { cn } from '@/ui/cn';
import { PLATFORM_NAME, PlatformLogo } from '@/ui/PlatformLogo';
import { DashMobileNav } from './DashMobileNav';
import { groupItems } from './navGroups';

/**
 * Shell for the signed-in back office: a deep purple sidebar beside the work
 * area, as drawn in docs/ui-wireframe/saarthi-wireframes.html.
 *
 * Every role shares it and differs only in navigation. An item may carry a
 * `group` (consecutive items with the same group sit under one heading) and a
 * `badge` count. Items whose backend is not built yet render as dimmed rows
 * marked SOON rather than links to a dead page - a nav that lies about what
 * exists is worse than one that admits the gap.
 *
 * Below 768px it behaves like an app, not a shrunk website: a sticky top bar
 * naming the current page, a tab bar along the bottom with the first four
 * places, and "More" (or the avatar) raising a bottom sheet with everything
 * else. No hamburger and no side drawer.
 */
export function DashLayout({ title, navItems = [], hideMobileNav = false, banner = null }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const { pathname } = useLocation();
  const current = currentItem(navItems, pathname);
  const scrolled = useScrolledPast(56);
  // Deeper than a nav item (an order, a vendor, a product): an app shows a
  // back arrow and the section's name instead of the app's.
  const isDetail = Boolean(current) && pathname.replace(/\/$/, '') !== current.to;

  async function handleLogout() {
    await logout();
    navigate('/', { replace: true });
  }

  const initials = (user?.full_name ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

  return (
    <div className="min-h-screen bg-surface-muted md:grid md:grid-cols-[248px_minmax(0,1fr)]">
      <aside className="no-scrollbar hidden bg-ink text-white md:sticky md:top-0 md:flex md:h-screen md:flex-col md:overflow-y-auto md:py-[18px] md:pl-3.5">
        <div className="mr-3.5 flex items-center gap-3 border-b border-white/10 pb-4 pl-1 pr-3.5">
          <PlatformLogo size={40} />
          <div className="min-w-0">
            <p className="font-display text-[15px] font-semibold leading-tight tracking-[-0.01em]">
              {PLATFORM_NAME}
            </p>
            <span className="mt-1 inline-block rounded-[5px] bg-white/10 px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-brand-200">
              {title}
            </span>
          </div>
        </div>

        <nav aria-label="Main" className="flex flex-col">
          {groupItems(navItems).map((group, index) => (
            <div key={group.heading ?? `group-${index}`} className="mt-4">
              {group.heading && (
                <h6 className="px-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-brand-300">
                  {group.heading}
                </h6>
              )}
              {group.items.map((item) =>
                item.disabled ? (
                  <DisabledItem key={item.label} item={item} />
                ) : (
                  <NavItem key={item.to} item={item} />
                ),
              )}
            </div>
          ))}
        </nav>

        <div className="mr-3.5 mt-auto flex items-center gap-2.5 border-t border-white/10 pt-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-accent-500 text-[13px] font-bold">
            {initials}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{user?.full_name}</p>
            <p className="truncate text-xs text-brand-200">{user?.email}</p>
          </div>
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface-muted/90 px-4 backdrop-blur-md md:static md:h-16 md:bg-surface-muted md:px-7 md:backdrop-blur-none">
          {/* Phone: an app bar - which app, and where in it. */}
          {/* At the top of a page its big heading names it, so the bar names
              the app; once that heading scrolls away the bar takes over the
              page name - the iOS large-title pattern. */}
          {isDetail && (
            <span className="flex min-w-0 items-center gap-1 md:hidden">
              <button
                type="button"
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(current.to))}
                aria-label={`Back to ${current.label}`}
                className="-ml-2 grid h-10 w-10 shrink-0 place-items-center rounded-full text-content transition-colors active:bg-surface"
              >
                <ArrowLeft className="h-[22px] w-[22px]" aria-hidden="true" />
              </button>
              <span className="truncate font-display text-[17px] font-bold text-content">
                {current.label}
              </span>
            </span>
          )}
          <span className={cn('flex min-w-0 items-center gap-2.5 md:hidden', isDetail && 'hidden')}>
            <PlatformLogo size={32} />
            <span className="relative min-w-0 flex-1 leading-tight">
              <span
                className={cn(
                  'block truncate transition-all duration-200',
                  scrolled ? '-translate-y-1 opacity-0' : 'opacity-100',
                )}
                aria-hidden={scrolled}
              >
                <span className="block truncate font-display text-[15px] font-bold text-content">
                  {PLATFORM_NAME}
                </span>
                <span className="block truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-content-muted">
                  {title}
                </span>
              </span>
              <span
                className={cn(
                  'absolute inset-0 flex items-center truncate font-display text-[17px] font-bold text-content transition-all duration-200',
                  scrolled ? 'opacity-100' : 'pointer-events-none translate-y-1 opacity-0',
                )}
                aria-hidden={!scrolled}
              >
                {current?.label ?? title}
              </span>
            </span>
          </span>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <div className="hidden text-right md:block">
              <p className="text-sm font-semibold text-content">{user?.full_name}</p>
              <p className="text-xs text-content-muted">{roleLabel(user?.role)}</p>
            </div>
            {/* Phone: the avatar opens the account sheet, where sign out lives. */}
            {!hideMobileNav && (
              <button
                type="button"
                onClick={openMenu}
                aria-label="Account and all pages"
                aria-expanded={menuOpen}
                className="grid h-9 w-9 place-items-center rounded-full bg-accent-500 text-[13px] font-bold text-white transition-transform active:scale-95 md:hidden"
              >
                {initials}
              </button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleLogout}
              // Phones sign out from the More sheet, or the account page.
              className="hidden md:inline-flex"
              leftIcon={<LogOut className="h-4 w-4" aria-hidden="true" />}
            >
              Sign out
            </Button>
          </div>
        </header>

        {banner}

        <main
          className={cn(
            'min-w-0 flex-1 px-4 pt-5 md:px-[30px] md:pb-10 md:pt-7',
            // Room for the bottom bar on phones, as the wireframe's 96px.
            hideMobileNav ? 'pb-8' : 'pb-24',
          )}
        >
          <Outlet />
        </main>
      </div>

      {!hideMobileNav && (
        <DashMobileNav
          navItems={navItems}
          title={title}
          open={menuOpen}
          onOpen={openMenu}
          onClose={closeMenu}
        />
      )}
    </div>
  );
}

/** True once the window has scrolled more than `offset` pixels. */
function useScrolledPast(offset) {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const update = () => setPast(window.scrollY > offset);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [offset]);
  return past;
}

/** The nav item for the page on screen: the most specific path that matches. */
function currentItem(items, pathname) {
  return items
    .filter((item) => item.to && matchPath({ path: item.to, end: Boolean(item.end) }, pathname))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

const ROLE_LABELS = {
  SUPER_ADMIN: 'Platform owner',
  ADMIN: 'Shop admin',
  VENDOR: 'Seller',
  CUSTOMER: 'Customer',
};

function roleLabel(role) {
  return ROLE_LABELS[role] ?? role;
}

function NavItem({ item }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-2.5 rounded-l-xl px-3 py-2.5 text-sm transition-all duration-200',
          isActive
            ? 'nav-active bg-surface-muted font-bold text-brand-500'
            : 'font-medium text-brand-100/90 hover:bg-white/[0.07] hover:pl-[15px] hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          {Icon && <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />}
          <span className="truncate">{item.label}</span>
          {item.badge > 0 && (
            <span
              className={cn(
                'ml-auto mr-2 rounded-full px-2 text-[11px] font-bold text-white',
                isActive ? 'bg-brand-500' : 'bg-accent-500',
              )}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function DisabledItem({ item }) {
  const Icon = item.icon;
  return (
    <span
      aria-disabled="true"
      title={item.note ?? 'Not available yet'}
      className="flex cursor-not-allowed items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-brand-100 opacity-40"
    >
      {Icon && <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />}
      <span className="truncate">{item.label}</span>
      <span className="ml-auto mr-2 rounded-[5px] border border-white/25 px-1.5 text-[10px] font-bold tracking-[0.05em]">
        {item.disabledTag ?? 'SOON'}
      </span>
    </span>
  );
}
